import React, {useCallback, useEffect, useMemo} from 'react';
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom';

import {useEventTracking} from 'src/hooks/useEventTracking';
import {SsoStatus} from 'src/openapi/admin/models/SsoStatus';

import {ConfirmLoginFields} from './components/confirm-login-fields/confirm-login-fields';
import {DownloadSpMetadata} from './components/download-sp-metadata/download-sp-metadata';
import {ExistingConnection} from './components/existing-connection/existing-connection';
import {GetStarted} from './components/get-started/get-started';
import {ReviewAndEnable} from './components/review-and-enable/review-and-enable';
import {SelectIdp} from './components/select-idp/select-idp';
import {SetupComplete} from './components/setup-complete/setup-complete';
import {TestEndUser} from './components/test-end-user/test-end-user';
import {UploadMetadata} from './components/upload-metadata/upload-metadata';
import {VerifyDomain} from './components/verify-domain/verify-domain';
import {CONNECTION_PATH, SETUP_COMPLETE_PATH, SSO_BASE_PATH, SSO_STEPS} from './constants';
import {useSaveDraftStep} from './hooks/use-save-draft-step';
import {clearSsoSessionStorage, hasWizardSessionState} from './hooks/use-sso-session-storage';
import {GuardedRoute, markStepReached} from './hooks/use-step-guard';
import {
    buildUpdateRequestForStep,
    getSsoStepFromIndex,
    getStepIndexFromSsoStep,
    isDomainStep,
    populateSessionStorageFromDraft,
    removeDeselectedDomainsFromStorage,
} from './utils/draft-session-storage-mapper';

import type {ConnectionSettingsResponse} from 'src/openapi/admin/models/ConnectionSettingsResponse';
import type {DraftConnectionResponse} from 'src/openapi/admin/models/DraftConnectionResponse';

type SsoSettingsRouterProps = {
    connection: ConnectionSettingsResponse | null,
    error: string | null,
    draft: DraftConnectionResponse | null,
};

function useStepNavigation(stepIndex: number) {
    const navigate = useNavigate();
    const {saveDraft, saving} = useSaveDraftStep();

    const onContinue = useCallback(async () => {
        const request = buildUpdateRequestForStep(stepIndex);
        const isLastStep = stepIndex >= SSO_STEPS.length - 1;

        const success = await saveDraft(request);
        if (!success) {
            return false;
        }
        if (isDomainStep(getSsoStepFromIndex(stepIndex))) {
            removeDeselectedDomainsFromStorage();
        }
        markStepReached(Math.min(stepIndex + 1, SSO_STEPS.length - 1));
        if (isLastStep) {
            navigate(`${SSO_BASE_PATH}/${SETUP_COMPLETE_PATH}`);
        } else {
            navigate(`${SSO_BASE_PATH}/${SSO_STEPS[stepIndex + 1].path}`);
        }
        return true;
    }, [stepIndex, navigate, saveDraft]);

    const onBack = useCallback(() => {
        if (stepIndex > 0) {
            navigate(`${SSO_BASE_PATH}/${SSO_STEPS[stepIndex - 1].path}`);
        }
    }, [stepIndex, navigate]);

    return {onContinue, onBack, saving};
}

function useTrackWizardStepOpen(stepIndex: number) {
    const {ssoWizardStepOpen} = useEventTracking();

    useEffect(() => {
        const currentStep = SSO_STEPS[stepIndex];
        if (currentStep == null) {
            return;
        }

        ssoWizardStepOpen({
            currentStepName: currentStep.label,
            nextStepName: SSO_STEPS[stepIndex + 1]?.label ?? '',
            previousStepName: SSO_STEPS[stepIndex - 1]?.label ?? '',
        });
    }, [ssoWizardStepOpen, stepIndex]);
}

const GetStartedRoute: React.FC = () => {
    const {onContinue, saving} = useStepNavigation(0);
    useTrackWizardStepOpen(0);
    return <GetStarted onContinue={onContinue} saving={saving}/>;
};

const SelectIdpRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(1);
    useTrackWizardStepOpen(1);
    return <SelectIdp onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const VerifyDomainRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(2);
    useTrackWizardStepOpen(2);
    return <VerifyDomain onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const DownloadSpMetadataRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(3);
    useTrackWizardStepOpen(3);
    return <DownloadSpMetadata onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const ConfirmLoginFieldsRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(4);
    useTrackWizardStepOpen(4);
    return <ConfirmLoginFields onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const UploadMetadataRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(5);
    useTrackWizardStepOpen(5);
    return <UploadMetadata onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const TestEndUserRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(6);
    useTrackWizardStepOpen(6);
    return <TestEndUser onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const ReviewAndEnableRoute: React.FC = () => {
    const {onContinue, onBack, saving} = useStepNavigation(7);
    useTrackWizardStepOpen(7);
    return <ReviewAndEnable onContinue={onContinue} onBack={onBack} saving={saving}/>;
};

const IndexRedirect: React.FC<SsoSettingsRouterProps> = ({connection, draft}) => {
    const draftFinished = draft == null || draft.status === SsoStatus.FINALIZED;

    if (draftFinished && connection != null && connection.ssoEnabled && !hasWizardSessionState()) {
        clearSsoSessionStorage();
        return <Navigate to={`${SSO_BASE_PATH}/${CONNECTION_PATH}`} replace={true}/>;
    }

    if (draft != null && !draftFinished && !hasWizardSessionState()) {
        populateSessionStorageFromDraft(draft, connection);
        const stepIndex = getStepIndexFromSsoStep(draft.latestStep);
        const targetStepIndex = stepIndex >= 0 ? Math.min(stepIndex + 1, SSO_STEPS.length - 1) : 0;
        markStepReached(targetStepIndex);
        const targetPath = SSO_STEPS[targetStepIndex].path;
        return <Navigate to={`${SSO_BASE_PATH}/${targetPath}`} replace={true}/>;
    }

    return <GetStartedRoute/>;
};

const ConnectionRoute: React.FC<SsoSettingsRouterProps> = ({connection, error, draft}) => {
    const draftInProgress = draft != null && draft.status !== SsoStatus.FINALIZED;
    if (connection == null || draftInProgress) {
        return <Navigate to={`${SSO_BASE_PATH}/${SSO_STEPS[0].path}`} replace={true}/>;
    }
    return <ExistingConnection connection={connection} error={error}/>;
};

export const SsoSettingsRouter: React.FC<SsoSettingsRouterProps> = ({connection, error, draft}) => {
    const indexRedirect = useMemo(
        () => <IndexRedirect connection={connection} error={error} draft={draft}/>,
        [connection, error, draft],
    );
    const connectionRoute = useMemo(
        () => <ConnectionRoute connection={connection} error={error} draft={draft}/>,
        [connection, error, draft],
    );

    return (
        <Routes>
            <Route index={true} element={indexRedirect}/>
            <Route path={SSO_STEPS[0].path} element={<GetStartedRoute/>}/>
            <Route
                path={SSO_STEPS[1].path}
                element={<GuardedRoute stepIndex={1}><SelectIdpRoute/></GuardedRoute>}
            />
            <Route
                path={SSO_STEPS[2].path}
                element={<GuardedRoute stepIndex={2}><VerifyDomainRoute/></GuardedRoute>}
            />
            <Route
                path={SSO_STEPS[3].path}
                element={<GuardedRoute stepIndex={3}><DownloadSpMetadataRoute/></GuardedRoute>}
            />
            <Route
                path={SSO_STEPS[4].path}
                element={<GuardedRoute stepIndex={4}><ConfirmLoginFieldsRoute/></GuardedRoute>}
            />
            <Route
                path={SSO_STEPS[5].path}
                element={<GuardedRoute stepIndex={5}><UploadMetadataRoute/></GuardedRoute>}
            />
            <Route
                path={SSO_STEPS[6].path}
                element={<GuardedRoute stepIndex={6}><TestEndUserRoute/></GuardedRoute>}
            />
            <Route
                path={SSO_STEPS[7].path}
                element={<GuardedRoute stepIndex={7}><ReviewAndEnableRoute/></GuardedRoute>}
            />
            <Route path={SETUP_COMPLETE_PATH} element={<SetupComplete/>}/>
            <Route path={CONNECTION_PATH} element={connectionRoute}/>
            <Route
                path="*"
                element={<Navigate to={`${SSO_BASE_PATH}/${SSO_STEPS[0].path}`} replace={true}/>}
            />
        </Routes>
    );
};
